import { useState } from 'react';
import type { VehicleInput, ScenarioInput } from '../types';

// Phase 25: input completeness indicator. Shown above the Compute button in
// both Personal and Fleet editors. Surfaces which fields are still at their
// system defaults so the user can review them before running TCO. Scope is
// intentionally narrow (5 fields) — flagging every possible field would just
// generate noise.
//
// Detection rules (all coarse, default-state heuristics — not perfect
// "user_set" tracking; the data model has no _user_set flag today):
//   • Insurance:   annual_insurance_premium <= 0
//   • MPG:         mpg_data_source == 'manufacturer' (i.e. not EPA/user override)
//   • Residual:    residual_data_source in {null, 'argonne_anl', 'estimated_class_curve'}
//   • Maintenance: annual_maintenance_cost <= 0
//   • Fuel price:  per-vehicle fuel_cost_per_gallon <= 0 AND no scenario-level override
//
// Fields where the user has explicitly entered a non-default value pass —
// equality with the default would still mark them as user-set since the
// detector only looks at the "still empty/auto" state.

export interface FlaggedField {
  key: string;
  label: string;
  defaultDescription: string;
  vehicleLabels: string[];
}

export function detectDefaultFields(scenario: ScenarioInput): FlaggedField[] {
  const vehicles: VehicleInput[] =
    scenario.mode === 'fleet' && Array.isArray(scenario.line_items)
      ? scenario.line_items.map((li) => li.vehicle)
      : scenario.vehicles;

  if (vehicles.length === 0) return [];

  const flagged: FlaggedField[] = [];

  // 1. Insurance — premium of 0 means the user never entered one.
  const insMissing = vehicles.filter(
    (v) => !((v.operating_costs.annual_insurance_premium ?? 0) > 0),
  );
  if (insMissing.length > 0) {
    flagged.push({
      key: 'insurance',
      label: 'Insurance premium',
      defaultDescription: '$0/yr — no premium entered',
      vehicleLabels: insMissing.map((v) => v.label || 'Unnamed vehicle'),
    });
  }

  // 2. MPG — manufacturer-spec rather than EPA-tested or user-override.
  const mpgMfr = vehicles.filter((v) => v.mpg_data_source === 'manufacturer');
  if (mpgMfr.length > 0) {
    flagged.push({
      key: 'mpg',
      label: 'Fuel economy (MPG)',
      defaultDescription: 'Manufacturer spec — not real-world EPA',
      vehicleLabels: mpgMfr.map((v) => v.label || 'Unnamed vehicle'),
    });
  }

  // 3. Residual — Argonne model or no source set (defaults to model).
  const residualEstimated = vehicles.filter((v) => {
    const src = v.residual_data_source;
    return src == null || src === 'argonne_anl' || src === 'estimated_class_curve';
  });
  if (residualEstimated.length > 0) {
    flagged.push({
      key: 'residual',
      label: 'Residual value',
      defaultDescription: 'Argonne ANL model estimate',
      vehicleLabels: residualEstimated.map((v) => v.label || 'Unnamed vehicle'),
    });
  }

  // 4. Maintenance — 0 means the user never entered an annual cost.
  const maintMissing = vehicles.filter(
    (v) => !((v.operating_costs.annual_maintenance_cost ?? 0) > 0),
  );
  if (maintMissing.length > 0) {
    flagged.push({
      key: 'maintenance',
      label: 'Annual maintenance',
      defaultDescription: '$0/yr — using default rate',
      vehicleLabels: maintMissing.map((v) => v.label || 'Unnamed vehicle'),
    });
  }

  // 5. Fuel price — per-vehicle 0/null AND no scenario-level override for
  //    the vehicle's fuel type. We treat any per-vehicle non-zero price as
  //    user-set, since the demo seeds explicitly set 0.0 to mean "use EIA".
  const hasScenarioFuelOverride = (fuelType: string): boolean => {
    if (fuelType === 'diesel') {
      return (scenario.scenario_diesel_price_per_gallon ?? 0) > 0;
    }
    if (fuelType === 'electric') {
      return (scenario.scenario_electricity_rate_per_kwh ?? 0) > 0;
    }
    // gasoline + phev share the gasoline override
    return (scenario.scenario_fuel_cost_per_gallon ?? 0) > 0;
  };
  const fuelDefault = vehicles.filter((v) => {
    if (hasScenarioFuelOverride(v.operating_costs.fuel_type)) return false;
    if (v.operating_costs.fuel_type === 'electric') {
      return !((v.operating_costs.electricity_cost_per_kwh ?? 0) > 0);
    }
    return !((v.operating_costs.fuel_cost_per_gallon ?? 0) > 0);
  });
  if (fuelDefault.length > 0) {
    flagged.push({
      key: 'fuel_price',
      label: 'Fuel price',
      defaultDescription: 'EIA weekly average (live fallback)',
      vehicleLabels: fuelDefault.map((v) => v.label || 'Unnamed vehicle'),
    });
  }

  return flagged;
}

export default function InputCompletenessIndicator({
  scenario,
}: {
  scenario: ScenarioInput;
}) {
  const flagged = detectDefaultFields(scenario);
  const [expanded, setExpanded] = useState(false);

  const hasVehicles =
    (scenario.mode === 'fleet'
      ? (scenario.line_items?.length ?? 0)
      : scenario.vehicles.length) > 0;
  if (!hasVehicles) return null;

  // Phase 25: when no fields are at default, show a quiet confirmation.
  if (flagged.length === 0) {
    return (
      <div
        data-testid="input-completeness"
        className="cnc-panel rounded-lg px-4 py-2.5 mb-3 flex items-center gap-2"
        style={{ background: 'var(--panel2)', border: '1px solid var(--border)' }}
      >
        <span style={{ color: 'var(--tco-accent, #4ade80)', fontSize: 14 }}>✓</span>
        <span className="text-xs" style={{ color: 'var(--white-dim)' }}>
          All inputs reviewed
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="input-completeness"
      className="cnc-panel rounded-lg px-4 py-3 mb-3"
      style={{ background: 'var(--panel2)', border: '1px solid var(--border)' }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <div className="flex items-center gap-3">
          <span
            data-testid="input-completeness-badge"
            className="inline-flex items-center justify-center text-xs font-semibold rounded-full"
            style={{
              background: 'var(--tco-line-soft, rgba(255,255,255,0.1))',
              color: 'var(--tco-ink, #e5e7eb)',
              minWidth: 22,
              height: 22,
              padding: '0 8px',
            }}
          >
            {flagged.length}
          </span>
          <span
            className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: 'var(--white-dim, #94a3b8)' }}
          >
            {flagged.length === 1
              ? '1 field using a default'
              : `${flagged.length} fields using defaults`}
          </span>
        </div>
        <span
          aria-hidden
          style={{
            color: 'var(--white-dim, #94a3b8)',
            fontSize: 14,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        >
          ›
        </span>
      </button>

      {expanded && (
        <ul style={{ marginTop: 10, listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {flagged.map((f) => (
            <li
              key={f.key}
              data-testid={`input-completeness-row-${f.key}`}
              style={{
                fontSize: 12,
                color: 'var(--white-dim, #94a3b8)',
                paddingLeft: 30,
                lineHeight: 1.45,
              }}
            >
              <div>
                <span style={{ color: 'var(--tco-ink, #e5e7eb)', fontWeight: 500 }}>{f.label}</span>
                <span style={{ marginLeft: 6 }}>· {f.defaultDescription}</span>
              </div>
              {f.vehicleLabels.length > 0 && (
                <div style={{ marginTop: 2, fontSize: 11, color: 'var(--white-dim, #6b7280)' }}>
                  {f.vehicleLabels.slice(0, 3).join(', ')}
                  {f.vehicleLabels.length > 3 && ` +${f.vehicleLabels.length - 3} more`}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
